import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  ADMIN_USER_ROLE_TIER,
  type AdminUserRole,
} from '@shared/types';
import { useAuthStore } from '../stores/authStore';
import { useGameStore } from '../stores/gameStore';
import { surfaceNavItems } from '../games/registry';
import type { AdminNavGroup } from '../games/types';
import ToastViewport from '../components/Toast';
import Pill, { type PillVariant } from '../components/Pill';

interface NavItem {
  to: string;
  label: string;
  /**
   * Minimum tier (by tier number) required to see this item. Defaults to the
   * `viewer` tier so the lateral `content_manager` role (tier 0) is hidden
   * from every general surface unless it is explicitly named in `roles`.
   */
  minTier?: number;
  /**
   * Extra roles that may see this item REGARDLESS of tier — the OR-half that
   * mirrors `RequireAdmin.allowRoles`. Used to surface the content pages to
   * `content_manager` without dragging in the tier-gated surfaces.
   */
  roles?: readonly AdminUserRole[];
  /**
   * Per-game admin surface this item belongs to (#619). Hidden when the
   * ACTIVE game's module doesn't declare the surface — mirroring the
   * server's requireGameSurface 404 on the same routes.
   */
  surface?: string;
}

interface NavGroup {
  heading: AdminNavGroup;
  items: ReadonlyArray<NavItem>;
}

/**
 * Sidebar nav grouped by operational purpose (UX-architect audit finding F4).
 * Platform items only; each group is led by the registered games' own items
 * for it (#817 — their admin manifests' `navItems`, surface-gated below).
 */
const PLATFORM_NAV_GROUPS: ReadonlyArray<NavGroup> = [
  {
    heading: 'Overview',
    items: [{ to: '/dashboard', label: 'Dashboard' }],
  },
  {
    heading: 'Content',
    items: [
      // Matches + Leaderboard are viewer-tier and NOT content_manager's remit.
      { to: '/matches', label: 'Matches' },
      // Live queue behind the dashboard's "In matchmaking" card.
      { to: '/matchmaking', label: 'Matchmaking Queue' },
      { to: '/leaderboard', label: 'Leaderboard' },
    ],
  },
  {
    heading: 'People & Safety',
    items: [
      { to: '/users', label: 'Users' },
      {
        to: '/moderation',
        label: 'Moderation',
        minTier: ADMIN_USER_ROLE_TIER.admin,
      },
      {
        to: '/admin-users',
        label: 'Admin Users',
        minTier: ADMIN_USER_ROLE_TIER.superadmin,
      },
      { to: '/audit', label: 'Audit Log' },
    ],
  },
  {
    heading: 'Operations',
    items: [
      { to: '/economy', label: 'Economy' },
      // Purchase rows behind the dashboard's IAP cards. Admin-tier: these are
      // identified buyer rows, same class as /users.
      { to: '/purchases', label: 'Purchases', minTier: ADMIN_USER_ROLE_TIER.admin },
      {
        to: '/remote-config',
        label: 'Remote Config',
        minTier: ADMIN_USER_ROLE_TIER.admin,
      },
      {
        to: '/broadcasts',
        label: 'Broadcasts',
        minTier: ADMIN_USER_ROLE_TIER.admin,
      },
      {
        to: '/support',
        label: 'Support Messages',
        minTier: ADMIN_USER_ROLE_TIER.admin,
      },
    ],
  },
];

const NAV_GROUPS: ReadonlyArray<NavGroup> = PLATFORM_NAV_GROUPS.map((group) => ({
  ...group,
  items: [...surfaceNavItems(group.heading), ...group.items],
}));

function roleVariant(role: string): PillVariant {
  if (role === 'superadmin') return 'accent';
  if (role === 'admin') return 'info';
  if (role === 'content_manager') return 'warning';
  if (role === 'viewer') return 'neutral';
  return 'neutral';
}

/**
 * Hamburger glyph rendered as SVG so it scales cleanly with font-size and
 * doesn't pull a font dep just for one icon. The 24x24 viewBox + 44x44 hit
 * target via the parent button keeps it touch-friendly on mobile.
 */
function HamburgerIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {open ? (
        <>
          <line x1="6" y1="6" x2="18" y2="18" />
          <line x1="6" y1="18" x2="18" y2="6" />
        </>
      ) : (
        <>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </>
      )}
    </svg>
  );
}

export default function AdminLayout() {
  const adminUser = useAuthStore((s) => s.adminUser);
  const logout = useAuthStore((s) => s.logout);
  const activeGameId = useGameStore((s) => s.activeGameId);
  const games = useGameStore((s) => s.games);
  const setActiveGame = useGameStore((s) => s.setActiveGame);
  const loadGames = useGameStore((s) => s.loadGames);
  const gamesLoaded = useGameStore((s) => s.gamesLoaded);
  const activeGame = games.find((g) => g.gameId === activeGameId);
  // ONLY admissible games may be offered. `X-Game-Id` rides every admin call
  // including `/auth/admin-login`, and `resolveGameMiddleware` 404s a game
  // failing either gate — so a one-click selection of a disabled or
  // not-deployed-here game persists to localStorage and locks the operator
  // out of the console until they hand-clear it. The excluded entries are
  // named below the control rather than silently dropped, so an operator
  // looking for a game they know exists is told why it is missing.
  const selectableGames = games.filter((g) => g.enabled && g.processEnabled);
  const unavailableGames = games.filter((g) => !g.enabled || !g.processEnabled);
  useEffect(() => {
    // Best-effort: a single-game deployment renders identically if this
    // fails (no switcher, default brand).
    loadGames().catch(() => {});
  }, [loadGames]);
  const navigate = useNavigate();
  const location = useLocation();

  // Off-canvas drawer state. Only meaningful below `md`; above `md` the
  // sidebar is statically positioned and this flag is ignored.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerCloseRef = useRef(() => setDrawerOpen(false));

  // Close the drawer on every route change so tapping a nav item dismisses
  // it without the user having to also tap the backdrop. We also clamp on
  // `Escape` for keyboard users.
  useEffect(() => {
    drawerCloseRef.current();
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setDrawerOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const role = adminUser?.role;
  const userTier = role ? ADMIN_USER_ROLE_TIER[role] : 0;
  // Visible when the user's tier clears the item's floor (default: viewer)
  // OR the user's role is explicitly allow-listed on the item. Same OR shape
  // as the `RequireAdmin` route guard, so nav and routing never disagree.
  // #619: surface-gated items vanish for games that don't declare the
  // surface. Until the games list loads, the active game keeps its surfaces
  // (no nav flicker for the incumbent).
  const surfaceOk = (item: NavItem): boolean =>
    !item.surface ||
    !gamesLoaded ||
    (activeGame?.surfaces?.includes(item.surface) ?? false);
  const canSee = (item: NavItem): boolean =>
    surfaceOk(item) &&
    (userTier >= (item.minTier ?? ADMIN_USER_ROLE_TIER.viewer) ||
      (role ? (item.roles?.includes(role) ?? false) : false));
  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(canSee),
  })).filter((group) => group.items.length > 0);

  async function handleLogout() {
    // logout() now hits the server to revoke the refresh-token session and
    // clear the HttpOnly cookie. We navigate AFTER the call resolves so
    // the next /admin-refresh probe (on Login.tsx hydrate) doesn't race
    // an in-flight cookie-clear and accidentally re-authenticate.
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-screen flex bg-slate-50 text-slate-900">
      {/* Backdrop — only renders + interactive on small screens with the
          drawer open. `md:hidden` keeps it out of the desktop tree entirely
          rather than trusting opacity to do the right thing. */}
      {drawerOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setDrawerOpen(false)}
          data-testid="admin-sidebar-backdrop"
          className="fixed inset-0 z-30 bg-slate-900/50 md:hidden"
        />
      )}

      <aside
        // Below md: fixed off-canvas drawer (translate-x-0 when open,
        // -translate-x-full when closed). Above md: regular flex column.
        // `transform` + `transition-transform` lets the slide animation
        // honor `prefers-reduced-motion` via the global CSS reset.
        className={[
          'fixed inset-y-0 left-0 z-40 w-64 transform bg-slate-800 text-slate-100',
          'transition-transform duration-200 ease-out',
          drawerOpen ? 'translate-x-0' : '-translate-x-full',
          // Static positioning + visible by default at md and up.
          'md:static md:translate-x-0 md:w-60 md:shrink-0',
          'flex flex-col',
        ].join(' ')}
        aria-label="Primary"
        aria-hidden={!drawerOpen ? undefined : false}
        data-testid="admin-sidebar"
      >
        <div className="px-4 py-5 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <img
              src={`${import.meta.env.BASE_URL}logo.png`}
              alt=""
              aria-hidden="true"
              data-testid="admin-sidebar-logo"
              className="w-8 h-8 rounded object-cover bg-slate-100 shrink-0"
            />
            <div className="min-w-0">
              <div
                className="text-sm font-semibold leading-tight truncate"
                data-testid="admin-active-game-name"
              >
                {activeGame?.displayName ?? ''}
              </div>
              <div className="text-xs text-slate-400 leading-tight truncate">
                Admin Backoffice
              </div>
            </div>
          </div>
          {/* Close button only on mobile — redundant with the backdrop but
              gives keyboard users a focusable target inside the drawer. */}
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close navigation"
            data-testid="admin-sidebar-close"
            className="md:hidden text-slate-300 hover:text-white p-2 -mr-2 rounded"
          >
            <HamburgerIcon open />
          </button>
        </div>

        {selectableGames.length > 1 && (
          /* #618: game switcher — rendered only when this deployment serves
             more than one SELECTABLE game, so a single-game admin looks
             exactly like today. Switching remounts the routed content via
             the Outlet key below, which refetches every list against the
             new game. */
          <div className="px-3 pt-3">
            <label htmlFor="admin-game-switcher" className="sr-only">
              Active game
            </label>
            <select
              id="admin-game-switcher"
              data-testid="admin-game-switcher"
              value={activeGameId}
              onChange={(e) => setActiveGame(e.target.value)}
              className="w-full rounded bg-slate-800 text-slate-100 text-sm px-2 py-1.5 border border-slate-700"
            >
              {selectableGames.map((g) => (
                <option key={g.gameId} value={g.gameId}>
                  {g.displayName}
                </option>
              ))}
            </select>
            {unavailableGames.length > 0 && (
              <p
                data-testid="admin-game-switcher-unavailable"
                className="mt-1 text-[10px] leading-tight text-slate-500"
              >
                Not available on this deployment:{' '}
                {unavailableGames
                  .map(
                    (g) =>
                      `${g.displayName} (${g.enabled ? 'not served by this server' : 'disabled'})`
                  )
                  .join(', ')}
              </p>
            )}
          </div>
        )}
        <nav aria-label="Admin" className="flex-1 p-2 space-y-4 overflow-y-auto">
          {visibleGroups.map((group) => (
            <div key={group.heading} className="space-y-1">
              <div
                className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500"
                data-testid={`nav-group-${group.heading
                  .toLowerCase()
                  .replace(/\s+/g, '-')}`}
              >
                {group.heading}
              </div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.to.replace('/', '')}`}
                  className={({ isActive }) =>
                    [
                      'block rounded px-3 py-2.5 text-sm transition-colors',
                      isActive
                        ? 'bg-slate-700 text-white'
                        : 'text-slate-300 hover:bg-slate-700/70 hover:text-white',
                    ].join(' ')
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header
          className="h-14 bg-white border-b border-slate-200 px-3 md:px-6 flex items-center gap-2 md:gap-4"
          data-testid="admin-header"
        >
          {/* Hamburger — mobile only. 44x44 hit target for touch. */}
          <button
            type="button"
            onClick={() => setDrawerOpen((v) => !v)}
            aria-label={drawerOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={drawerOpen}
            aria-controls="admin-sidebar"
            data-testid="admin-sidebar-toggle"
            className="md:hidden inline-flex items-center justify-center w-11 h-11 -ml-2 rounded text-slate-700 hover:bg-slate-100"
          >
            <HamburgerIcon open={drawerOpen} />
          </button>

          {/* Identity strip — collapses gracefully on small screens.
              Below sm, "Signed in as" label is hidden but display name and
              role pill stay (they're the load-bearing identity). The
              container `min-w-0 flex-1` lets the name truncate instead of
              pushing the logout button off-screen. */}
          <div className="flex-1 min-w-0 flex items-center gap-2 text-sm text-slate-500 overflow-hidden">
            <span className="hidden sm:inline whitespace-nowrap">
              Signed in as
            </span>
            <span
              className="font-medium text-slate-900 truncate"
              data-testid="admin-header-display-name"
            >
              {adminUser?.displayName ?? adminUser?.username ?? 'unknown'}
            </span>
            {adminUser?.role && (
              <span className="inline-block shrink-0">
                <Pill
                  variant={roleVariant(adminUser.role)}
                  testId="admin-header-role"
                >
                  {adminUser.role}
                </Pill>
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={handleLogout}
            data-testid="admin-logout"
            className="shrink-0 text-sm text-slate-700 hover:text-slate-900 border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 whitespace-nowrap"
          >
            Log out
          </button>
        </header>

        <main
          className="flex-1 p-4 md:p-6 overflow-auto"
          data-testid="admin-content"
        >
          <Outlet key={activeGameId} />
        </main>
      </div>

      <ToastViewport />
    </div>
  );
}
